import { userField } from '../userField';

export const MARKETPLACE_CATEGORIES = {
  yoga: 'yoga',
  meditation: 'meditation',
  breathwork: 'breathwork',
  energyHealing: 'energyHealing',
  soundHealing: 'soundHealing',
  tantra: 'tantra',
  chakras: 'chakras',
  music: 'music',
  dance: 'dance',
  cacao: 'cacao',
  ceremonies: 'ceremonies',
  spiritualGuidance: 'spiritualGuidance',
  teaCeremony: 'teaCeremony',
  healingRitual: 'healingRitual',
  healingCircle: 'healingCircle',
  other: 'other',
  retreat: 'retreat',
};

/**
 * Fallback homepage / chip-strip order when hosted listing-categories are absent.
 * Mirrors Sharetribe Console listing-categories, with Healing Circle immediately before Other.
 */
export const HOMEPAGE_CATEGORY_IDS = [
  MARKETPLACE_CATEGORIES.yoga,
  MARKETPLACE_CATEGORIES.meditation,
  MARKETPLACE_CATEGORIES.breathwork,
  MARKETPLACE_CATEGORIES.soundHealing,
  MARKETPLACE_CATEGORIES.energyHealing,
  MARKETPLACE_CATEGORIES.tantra,
  MARKETPLACE_CATEGORIES.music,
  MARKETPLACE_CATEGORIES.dance,
  MARKETPLACE_CATEGORIES.cacao,
  MARKETPLACE_CATEGORIES.spiritualGuidance,
  MARKETPLACE_CATEGORIES.teaCeremony,
  MARKETPLACE_CATEGORIES.healingRitual,
  MARKETPLACE_CATEGORIES.retreat,
  MARKETPLACE_CATEGORIES.healingCircle,
  MARKETPLACE_CATEGORIES.other,
];

export const HEALING_CIRCLE_CATEGORY = {
  id: MARKETPLACE_CATEGORIES.healingCircle,
  name: 'Healing Circle',
  subcategories: [],
};

export const HEALING_CIRCLE_USER_FIELD_OPTION = {
  option: MARKETPLACE_CATEGORIES.healingCircle,
  label: 'Healing Circle',
};

const getCategoryKey = item => {
  if (typeof item === 'string') {
    return item;
  }
  return item?.id || item?.option || null;
};

/**
 * Place `newItem` immediately before `other` (last/other tile). Dedupes if already present.
 * If `other` is missing, appends at the end.
 *
 * @param {Array<string|object>} items
 * @param {string|object} newItem
 * @returns {Array<string|object>}
 */
export const insertBeforeOther = (items, newItem) => {
  if (!Array.isArray(items)) {
    return items;
  }
  const newId = getCategoryKey(newItem);
  const existing = items.find(item => getCategoryKey(item) === newId);
  const itemToInsert = existing || newItem;
  const without = items.filter(item => getCategoryKey(item) !== newId);
  const otherIndex = without.findIndex(
    item => getCategoryKey(item) === MARKETPLACE_CATEGORIES.other
  );
  if (otherIndex === -1) {
    return [...without, itemToInsert];
  }
  return [...without.slice(0, otherIndex), itemToInsert, ...without.slice(otherIndex)];
};

/**
 * Inject Healing Circle into a hosted listing-categories tree so listing/search pickers
 * show it even before Console is updated. No-op on empty hosted config so the homepage
 * fallback list is used instead of a one-item strip.
 *
 * @param {Array<{ id: string, name?: string, subcategories?: Array }>} categories
 * @returns {Array}
 */
export const ensureHealingCircleInCategories = categories => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return Array.isArray(categories) ? categories : [];
  }
  return insertBeforeOther(categories, HEALING_CIRCLE_CATEGORY);
};

/**
 * Inject Healing Circle into the practitioner profile `categories` user-field enum.
 * No-op when that hosted field is missing or has no options.
 *
 * @param {Array} userFields
 * @returns {Array}
 */
export const ensureHealingCircleUserFieldOption = userFields => {
  if (!Array.isArray(userFields)) {
    return userFields;
  }
  return userFields.map(field => {
    if (field?.key !== userField.CATEGORIES || !Array.isArray(field.enumOptions)) {
      return field;
    }
    if (field.enumOptions.length === 0) {
      return field;
    }
    return {
      ...field,
      enumOptions: insertBeforeOther(field.enumOptions, HEALING_CIRCLE_USER_FIELD_OPTION),
    };
  });
};

/**
 * Homepage chip ids: hosted listing-categories when present (with Healing Circle
 * forced immediately before Other), otherwise HOMEPAGE_CATEGORY_IDS.
 *
 * @param {Array<{ id: string }>} hostedCategories
 * @returns {string[]}
 */
export const resolveHomepageCategoryIds = hostedCategories => {
  const fromHosted = hostedCategories?.map(category => category?.id).filter(Boolean);
  const base = fromHosted?.length > 0 ? fromHosted : HOMEPAGE_CATEGORY_IDS;
  return insertBeforeOther(base, MARKETPLACE_CATEGORIES.healingCircle);
};

/**
 * Find a category (or nested subcategory) by id in the hosted category tree.
 *
 * @param {Array<{ id: string, name?: string, subcategories?: Array }>} categories
 * @param {string} categoryId
 * @returns {{ id: string, name?: string, subcategories?: Array }|null}
 */
const findCategoryById = (categories, categoryId) => {
  if (!categories?.length || !categoryId) {
    return null;
  }
  for (const category of categories) {
    if (category.id === categoryId) {
      return category;
    }
    const nested = findCategoryById(category.subcategories, categoryId);
    if (nested) {
      return nested;
    }
  }
  return null;
};

/**
 * Resolve a category display label.
 * When intl is provided, prefers Category.{categoryId} translation keys
 * with the hosted-config name as defaultMessage.
 *
 * @param {string} categoryId
 * @param {Object} config
 * @param {Object} [intl]
 * @returns {string}
 */
export const getCategoryLabel = (categoryId, config, intl) => {
  const listingCategories = config?.categoryConfiguration?.categories;
  const category = findCategoryById(listingCategories, categoryId);
  const fallbackName = category?.name || '';

  if (intl && categoryId) {
    return intl.formatMessage({
      id: `Category.${categoryId}`,
      defaultMessage: fallbackName || categoryId,
    });
  }

  return fallbackName;
};
